import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { usePatientContext } from "@/contexts/PatientContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import QuickStartWizard, { useQuickStart } from "@/components/QuickStartWizard";
import AddToEHR from "@/components/AddToEHR";
import HospiceOSFeatures from "@/components/HospiceOSFeatures";
import CommandPaletteButton from "@/components/CommandPaletteButton";
import SmartSchedulingSuggestions from "@/components/SmartSchedulingSuggestions";
import PatientAcuityTrendsDashboard from "@/components/PatientAcuityTrendsDashboard";
import { 
  getStaffCaseload, getStaffPatientDetails, importCaseload, getCaseloadTemplate,
  assignStaffPatient, removeStaffAssignment, getStaffList, getAllPatientsList,
  createStaffConversation, getStaffConversations, getStaffConversationMessages, 
  sendStaffMessage, getStaffUnreadCount, getCaseloadMoodTrends, getStaffAppreciations,
  getSmsSettings, updateSmsSettings, getClinicalAlerts, dismissClinicalAlert,
  markStaffMessagesRead, setStaffTyping, getStaffTypingStatus, createPatientManually,
  createFHIRPatient, createFHIRCondition
} from "@/lib/api";
import { 
  Users, User, MessageSquare, Upload, Download, Search, Plus,
  AlertCircle, CheckCircle2, Clock, Activity, FileText, Calendar,
  Pill, Heart, ChevronRight, Send, X, Loader2, FolderOpen,
  UserPlus, Trash2, Phone, Mail, RefreshCw, TrendingUp, Star,
  Smile, Frown, Meh, AlertTriangle, ThumbsUp, Settings, Bell,
  GraduationCap, ClipboardCheck, Check, CheckCheck, Rocket, Shield,
  Command, Zap
} from "lucide-react";
import { Link } from "react-router-dom";

export default function StaffPortal() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Patient Context for Command Palette
  const { setActivePatient, currentPatient, setViewContext } = usePatientContext();
  
  // Quick Start Wizard
  const { showWizard, hasCompleted, openWizard, closeWizard, markComplete } = useQuickStart();
  
  const [activeTab, setActiveTab] = useState("caseload");
  const [loading, setLoading] = useState(true);
  const [caseload, setCaseload] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Assignment modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [patientsList, setPatientsList] = useState([]);
  const [assignmentData, setAssignmentData] = useState({
    staffId: "",
    patientId: "",
    type: "primary",
    notes: ""
  });
  
  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Messaging
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [newConversation, setNewConversation] = useState({
    patientId: "",
    subject: "",
    message: ""
  });
  const [patientTyping, setPatientTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const typingCheckIntervalRef = useRef(null);
  
  // Mood trends state
  const [moodTrends, setMoodTrends] = useState(null);
  const [moodTrendsLoading, setMoodTrendsLoading] = useState(false);
  const [appreciations, setAppreciations] = useState(null);
  
  // SMS Settings state
  const [smsSettings, setSmsSettings] = useState({ phone: "", sms_notifications: true });
  const [smsSettingsLoading, setSmsSettingsLoading] = useState(false);
  const [savingSmsSettings, setSavingSmsSettings] = useState(false);
  
  // Clinical Alerts state
  const [clinicalAlerts, setClinicalAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  
  // Manual Patient Creation state
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [addingPatient, setAddingPatient] = useState(false);
  const [newPatientData, setNewPatientData] = useState({
    name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    address: "",
    diagnosis: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    notes: "",
    assign_to_staff_id: ""
  });
  const [createdPatient, setCreatedPatient] = useState(null);
  
  const isStaff = user?.role && ["staff", "admin", "administrator", "provider"].includes(user.role);
  const isAdmin = user?.role && ["admin", "administrator"].includes(user.role);

  // Set view context for command palette
  useEffect(() => {
    setViewContext("caseload");
    return () => setViewContext("general");
  }, [setViewContext]);

  useEffect(() => {
    if (!authLoading && isStaff) {
      loadCaseload();
      loadConversations();
      loadUnreadCount();
      loadMoodTrends();
      loadAppreciations();
      loadSmsSettings();
      loadClinicalAlerts();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const loadCaseload = async () => {
    try {
      setLoading(true);
      const data = await getStaffCaseload();
      setCaseload(data.caseload || []);
    } catch (error) {
      console.error("Error loading caseload:", error);
      toast.error("Failed to load caseload");
    } finally {
      setLoading(false);
    }
  };

  const loadMoodTrends = async () => {
    try {
      setMoodTrendsLoading(true);
      const data = await getCaseloadMoodTrends(30);
      setMoodTrends(data);
    } catch (error) {
      console.error("Error loading mood trends:", error);
    } finally {
      setMoodTrendsLoading(false);
    }
  };

  const loadAppreciations = async () => {
    if (!user?.id) return;
    try {
      const data = await getStaffAppreciations(user.id);
      setAppreciations(data);
    } catch (error) {
      console.error("Error loading appreciations:", error);
    }
  };

  const loadSmsSettings = async () => {
    try {
      setSmsSettingsLoading(true);
      const data = await getSmsSettings();
      setSmsSettings({
        phone: data.phone || "",
        sms_notifications: data.sms_notifications !== false
      });
    } catch (error) {
      console.error("Error loading SMS settings:", error);
    } finally {
      setSmsSettingsLoading(false);
    }
  };

  const handleSaveSmsSettings = async () => {
    try {
      setSavingSmsSettings(true);
      await updateSmsSettings(smsSettings.phone, smsSettings.sms_notifications);
      toast.success("SMS notification settings saved");
    } catch (error) {
      console.error("Error saving SMS settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSavingSmsSettings(false);
    }
  };

  const loadClinicalAlerts = async () => {
    try {
      setAlertsLoading(true);
      const data = await getClinicalAlerts();
      setClinicalAlerts(data.alerts || []);
    } catch (error) {
      console.error("Error loading clinical alerts:", error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await dismissClinicalAlert(alertId);
      setClinicalAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success("Alert dismissed");
    } catch (error) {
      console.error("Error dismissing alert:", error);
      toast.error("Failed to dismiss alert");
    }
  };

  const loadConversations = async () => {
    try {
      const data = await getStaffConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const data = await getStaffUnreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error("Error loading unread count:", error);
    }
  };

  const viewPatientDetails = async (patient) => {
    setSelectedPatient(patient);
    setDetailsLoading(true);
    
    // Set patient context for command palette - THE GOLDEN THREAD
    setActivePatient(patient.patient_id, {
      id: patient.patient_id,
      name: patient.patient_name,
      mrn: patient.mrn
    });
    
    try {
      const data = await getStaffPatientDetails(patient.patient_id);
      setPatientDetails(data);
    } catch (error) {
      console.error("Error loading patient details:", error);
      toast.error("Failed to load patient details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleImportCaseload = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }
    
    setImporting(true);
    try {
      const results = await importCaseload(importFile);
      setImportResults(results);
      if (results.successful > 0) {
        toast.success(`Successfully imported ${results.successful} assignments`);
        loadCaseload();
      }
      if (results.failed > 0) {
        toast.warning(`${results.failed} rows failed to import`);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import caseload");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const data = await getCaseloadTemplate();
      const blob = new Blob([data.template], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'caseload_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const openAssignModal = async () => {
    if (!isAdmin) {
      toast.error("Only administrators can assign patients");
      return;
    }
    
    try {
      const [staffData, patientsData] = await Promise.all([
        getStaffList(),
        getAllPatientsList()
      ]);
      setStaffList(staffData.staff || []);
      setPatientsList(patientsData.patients || []);
      setShowAssignModal(true);
    } catch (error) {
      toast.error("Failed to load assignment data");
    }
  };

  const handleAssign = async () => {
    if (!assignmentData.staffId || !assignmentData.patientId) {
      toast.error("Please select both staff and patient");
      return;
    }
    
    try {
      await assignStaffPatient(
        assignmentData.staffId,
        assignmentData.patientId,
        assignmentData.type,
        assignmentData.notes || null
      );
      toast.success("Patient assigned successfully");
      setShowAssignModal(false);
      setAssignmentData({ staffId: "", patientId: "", type: "primary", notes: "" });
      loadCaseload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign patient");
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment?")) return;
    
    try {
      await removeStaffAssignment(assignmentId);
      toast.success("Assignment removed");
      loadCaseload();
    } catch (error) {
      toast.error("Failed to remove assignment");
    }
  };

  const handleAddPatient = async () => {
    if (!newPatientData.name || !newPatientData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newPatientData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setAddingPatient(true);
    try {
      // Step 1: Create patient in FHIR store (PHI data - Google Cloud Healthcare API)
      const nameParts = newPatientData.name.trim().split(" ");
      const givenName = nameParts[0] || "";
      const familyName = nameParts.slice(1).join(" ") || nameParts[0] || "";
      
      const fhirPatientData = {
        given_name: givenName,
        family_name: familyName,
        birth_date: newPatientData.date_of_birth || "1900-01-01",
        gender: "unknown",
        telephone: newPatientData.phone || null,
        email: newPatientData.email,
        address: newPatientData.address ? {
          line: newPatientData.address,
          city: "",
          state: "",
          postal_code: ""
        } : null
      };
      
      const fhirPatient = await createFHIRPatient(fhirPatientData);
      const fhirPatientId = fhirPatient.id;
      
      // Step 2: If diagnosis provided, create FHIR Condition
      if (newPatientData.diagnosis) {
        await createFHIRCondition({
          patient_id: fhirPatientId,
          code: "Z51.5", // Default hospice care code
          code_display: newPatientData.diagnosis,
          clinical_status: "active"
        });
      }
      
      // Step 3: Create patient record in MongoDB (non-PHI data + FHIR reference)
      const result = await createPatientManually({
        name: newPatientData.name,
        email: newPatientData.email,
        phone: newPatientData.phone || null,
        date_of_birth: newPatientData.date_of_birth || null,
        address: newPatientData.address || null,
        diagnosis: newPatientData.diagnosis || null,
        emergency_contact_name: newPatientData.emergency_contact_name || null,
        emergency_contact_phone: newPatientData.emergency_contact_phone || null,
        notes: newPatientData.notes || null,
        assign_to_staff_id: newPatientData.assign_to_staff_id || null,
        fhir_patient_id: fhirPatientId // Link to FHIR record
      });
      
      setCreatedPatient({
        ...result.patient,
        fhir_patient_id: fhirPatientId,
        sms_sent: result.sms_sent,
        sms_status: result.sms_status
      });
      
      if (result.sms_sent) {
        toast.success(`Patient ${newPatientData.name} created in FHIR store and SMS sent!`);
      } else {
        toast.success(`Patient ${newPatientData.name} created successfully in FHIR store!`);
      }
      
      // Reset form but keep modal open to show credentials
      setNewPatientData({
        name: "",
        email: "",
        phone: "",
        date_of_birth: "",
        address: "",
        diagnosis: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        notes: "",
        assign_to_staff_id: ""
      });
      
      loadCaseload();
    } catch (error) {
      console.error("Patient creation error:", error);
      toast.error(error.response?.data?.detail || "Failed to create patient");
    } finally {
      setAddingPatient(false);
    }
  };

  const closeAddPatientModal = () => {
    setShowAddPatientModal(false);
    setCreatedPatient(null);
    setNewPatientData({
      name: "",
      email: "",
      phone: "",
      date_of_birth: "",
      address: "",
      diagnosis: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      notes: "",
      assign_to_staff_id: ""
    });
  };

  const loadConversationMessages = async (conversation) => {
    setSelectedConversation(conversation);
    try {
      const data = await getStaffConversationMessages(conversation.id);
      setMessages(data.messages || []);
      loadUnreadCount();
      
      // Mark messages as read
      try {
        await markStaffMessagesRead(conversation.id);
      } catch (e) {
        // Silently fail
      }
      
      // Start polling for typing status
      checkTypingStatus(conversation.id);
      if (typingCheckIntervalRef.current) clearInterval(typingCheckIntervalRef.current);
      typingCheckIntervalRef.current = setInterval(() => checkTypingStatus(conversation.id), 3000);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const checkTypingStatus = async (conversationId) => {
    if (!conversationId) return;
    try {
      const status = await getStaffTypingStatus(conversationId);
      setPatientTyping(status.patient_typing);
    } catch (error) {
      // Silently fail
    }
  };

  const handleStaffTyping = async () => {
    if (!selectedConversation?.id) return;
    
    if (!isTyping) {
      setIsTyping(true);
      try {
        await setStaffTyping(selectedConversation.id, true);
      } catch (error) {
        // Silently fail
      }
    }
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    
    typingTimeoutRef.current = setTimeout(async () => {
      setIsTyping(false);
      try {
        await setStaffTyping(selectedConversation.id, false);
      } catch (error) {
        // Silently fail
      }
    }, 3000);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    // Stop typing indicator
    setIsTyping(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    try {
      await setStaffTyping(selectedConversation.id, false);
    } catch (err) {
      console.debug("Typing status update skipped:", err);
    }
    
    try {
      await sendStaffMessage(selectedConversation.id, newMessage);
      setNewMessage("");
      loadConversationMessages(selectedConversation);
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleCreateConversation = async () => {
    if (!newConversation.patientId || !newConversation.subject || !newConversation.message) {
      toast.error("Please fill all fields");
      return;
    }
    
    try {
      await createStaffConversation(
        newConversation.patientId,
        newConversation.subject,
        newConversation.message
      );
      toast.success("Conversation started");
      setShowNewMessageModal(false);
      setNewConversation({ patientId: "", subject: "", message: "" });
      loadConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start conversation");
    }
  };

  const filteredCaseload = caseload.filter(p =>
    p.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.patient_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">{t('staffPortal.accessDenied', 'Staff Access Required')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('staffPortal.accessDeniedDesc', 'This portal is for Goodwin Cares staff members only.')}
            </p>
            <Button onClick={() => navigate("/")}>{t('common.returnHome', 'Return Home')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="staff-portal">
      {/* Quick Start Wizard */}
      <QuickStartWizard 
        isOpen={showWizard} 
        onClose={closeWizard}
        onComplete={markComplete}
      />
      
      <div className="container-max px-6 md:px-12">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-foreground">{t('staffPortal.title', 'Staff Portal')}</h1>
            <p className="text-muted-foreground text-sm">
              {t('staffPortal.subtitle', 'Manage your patient caseload and communications')}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Command Palette Quick Access */}
            <CommandPaletteButton 
              variant="header"
              viewContext="caseload"
              userRole={user?.role || "staff"}
              patientContext={currentPatient ? {
                patient_id: currentPatient.id,
                patient_name: currentPatient.name
              } : null}
              showKeyboardHint={true}
            />
            {/* Quick Links */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/staff-supplies")}
              className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
              data-testid="supplies-link"
            >
              <Activity className="w-4 h-4 mr-2 text-emerald-600" />
              {t('staffPortal.orderSupplies', 'Order Supplies')}
            </Button>
            {/* Quick Start Button */}
            {!hasCompleted && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openWizard}
                className="bg-gradient-to-r from-primary/10 to-indigo-500/10 border-primary/30 hover:bg-primary/20"
                data-testid="quickstart-button"
              >
                <Rocket className="w-4 h-4 mr-2 text-primary" />
                {t('staffPortal.admin.quickStart', 'Quick Start')}
              </Button>
            )}
            <AddToEHR 
              buttonVariant="default"
              buttonSize="sm"
              buttonText={t('staffPortal.admin.addToEHR', 'Add to EHR')}
              className="bg-teal-600 hover:bg-teal-700"
            />
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('staffPortal.caseload.importCaseload', 'Import Caseload')}
                </Button>
                <Button size="sm" onClick={openAssignModal}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('staffPortal.caseload.assignPatient', 'Assign Patient')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('caseload')}
            data-testid="stat-my-patients"
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{caseload.length}</p>
                  <p className="text-xs text-muted-foreground">{t('staffPortal.stats.myPatients', 'My Patients')}</p>
                </div>
              </div>
              <p className="text-xs text-blue-500 mt-2">{t('staffPortal.stats.viewCaseload', 'View caseload')} →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('caseload')}
            data-testid="stat-active"
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {caseload.filter(p => p.last_checkin).length}
                  </p>
                  <p className="text-xs text-muted-foreground">{t('staffPortal.stats.activeThisWeek', 'Active This Week')}</p>
                </div>
              </div>
              <p className="text-xs text-green-500 mt-2">{t('staffPortal.stats.viewScheduled', 'View scheduled')} →</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveTab('messages')}
            data-testid="stat-conversations"
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{conversations.length}</p>
                  <p className="text-xs text-muted-foreground">{t('staffPortal.stats.conversations', 'Conversations')}</p>
                </div>
              </div>
              <p className="text-xs text-purple-500 mt-2">{t('staffPortal.stats.viewAllMessages', 'View all messages')} →</p>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${unreadCount > 0 ? "border-amber-200 bg-amber-50" : ""}`}
            onClick={() => setActiveTab('messages')}
            data-testid="stat-unread"
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${unreadCount > 0 ? 'bg-amber-200' : 'bg-gray-100'}`}>
                  <Mail className={`w-5 h-5 ${unreadCount > 0 ? 'text-amber-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{unreadCount}</p>
                  <p className="text-xs text-muted-foreground">{t('staffPortal.stats.unreadMessages', 'Unread Messages')}</p>
                </div>
              </div>
              <p className="text-xs text-amber-500 mt-2">{t('staffPortal.stats.viewUnread', 'View unread')} →</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions Panel */}
        <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-indigo-500/5" data-testid="quick-actions-panel">
          <CardHeader className="py-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              {t('staffPortal.quickActions.title', 'Quick Actions')}
              {(clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length > 0 || 
                caseload.filter(p => !p.last_checkin || (new Date() - new Date(p.last_checkin)) / (1000 * 60 * 60 * 24) > 3).length > 0) && (
                <Badge variant="destructive" className="ml-2">
                  {t('staffPortal.quickActions.attentionNeeded', 'Attention Needed')}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Urgent Clinical Alerts */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  {t('staffPortal.quickActions.urgentAlerts', 'Urgent Alerts')}
                  {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length > 0 && (
                    <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length}
                    </Badge>
                  )}
                </Label>
                {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").slice(0, 3).map((alert, idx) => (
                  <div 
                    key={alert.id || idx}
                    className="flex items-center justify-between p-2 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => {
                      setActiveTab('clinical-alerts');
                      toast.info(`Viewing alert for ${alert.patient_name}`);
                    }}
                    data-testid={`urgent-alert-${idx}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-red-800 truncate">{alert.patient_name}</p>
                        <p className="text-xs text-red-600 truncate">{alert.alert_type?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-red-400 flex-shrink-0" />
                  </div>
                ))}
                {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length === 0 && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xs text-emerald-700">{t('staffPortal.quickActions.noUrgentAlerts', 'No urgent alerts')}</p>
                  </div>
                )}
              </div>

              {/* Patients Needing Attention */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2 text-amber-600">
                  <Clock className="w-4 h-4" />
                  {t('staffPortal.quickActions.needsAttention', 'Needs Follow-up')}
                  {caseload.filter(p => !p.last_checkin || (new Date() - new Date(p.last_checkin)) / (1000 * 60 * 60 * 24) > 3).length > 0 && (
                    <Badge className="bg-amber-500 h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {caseload.filter(p => !p.last_checkin || (new Date() - new Date(p.last_checkin)) / (1000 * 60 * 60 * 24) > 3).length}
                    </Badge>
                  )}
                </Label>
                {caseload.filter(p => !p.last_checkin || (new Date() - new Date(p.last_checkin)) / (1000 * 60 * 60 * 24) > 3).slice(0, 3).map((patient, idx) => (
                  <div 
                    key={patient.id || idx}
                    className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                    onClick={() => viewPatientDetails(patient)}
                    data-testid={`attention-patient-${idx}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-800 truncate">{patient.patient_name}</p>
                        <p className="text-xs text-amber-600">
                          {patient.last_checkin 
                            ? `${Math.floor((new Date() - new Date(patient.last_checkin)) / (1000 * 60 * 60 * 24))} days ago`
                            : t('staffPortal.quickActions.noCheckins', 'No check-ins')
                          }
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  </div>
                ))}
                {caseload.filter(p => !p.last_checkin || (new Date() - new Date(p.last_checkin)) / (1000 * 60 * 60 * 24) > 3).length === 0 && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xs text-emerald-700">{t('staffPortal.quickActions.allFollowedUp', 'All patients followed up')}</p>
                  </div>
                )}
              </div>

              {/* Quick Action Buttons */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2 text-primary">
                  <Rocket className="w-4 h-4" />
                  {t('staffPortal.quickActions.actions', 'Actions')}
                </Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start gap-2 hover:bg-primary/10"
                  onClick={() => navigate("/hos")}
                  data-testid="qa-patient-timeline"
                >
                  <FileText className="w-4 h-4 text-primary" />
                  {t('staffPortal.quickActions.openTimeline', 'Open Patient Timeline')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start gap-2 hover:bg-primary/10"
                  onClick={() => setShowNewMessageModal(true)}
                  data-testid="qa-send-message"
                >
                  <MessageSquare className="w-4 h-4 text-purple-500" />
                  {t('staffPortal.quickActions.sendMessage', 'Send Patient Message')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start gap-2 hover:bg-primary/10"
                  onClick={() => setShowAddPatientModal(true)}
                  data-testid="qa-add-patient"
                >
                  <UserPlus className="w-4 h-4 text-emerald-500" />
                  {t('staffPortal.quickActions.addPatient', 'Add New Patient')}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full justify-start gap-2 hover:bg-primary/10"
                  onClick={() => navigate("/staff-supplies")}
                  data-testid="qa-order-supplies"
                >
                  <Activity className="w-4 h-4 text-teal-500" />
                  {t('staffPortal.quickActions.orderSupplies', 'Order Supplies')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Smart Scheduling Suggestions */}
        <SmartSchedulingSuggestions 
          className="mb-6"
          onScheduleVisit={(visit) => {
            toast.success(`Visit scheduled for ${visit?.patient_name || 'patient'}`);
            loadCaseload(); // Refresh caseload after scheduling
          }}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="caseload" className="gap-2">
              <Users className="w-4 h-4" />
              {t('staffPortal.tabs.caseload', 'My Caseload')}
            </TabsTrigger>
            <TabsTrigger value="mood-trends" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('staffPortal.tabs.moodTrends', 'Mood Trends')}
              {moodTrends?.alerts?.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {moodTrends.alerts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              {t('staffPortal.tabs.messages', 'Messages')}
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {unreadCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="clinical-alerts" className="gap-2">
              <ClipboardCheck className="w-4 h-4" />
              {t('staffPortal.tabs.alerts', 'Clinical Alerts')}
              {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {clinicalAlerts.filter(a => a.priority === "urgent" || a.priority === "high").length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="acuity-trends" className="gap-2">
              <Activity className="w-4 h-4" />
              {t('staffPortal.tabs.acuityTrends', 'Acuity Trends')}
            </TabsTrigger>
            <TabsTrigger value="appreciation" className="gap-2">
              <Star className="w-4 h-4" />
              {t('staffPortal.tabs.appreciations', 'Appreciation')}
              {appreciations?.stats?.total > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {appreciations.stats.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" />
              {t('staffPortal.tabs.settings', 'Settings')}
            </TabsTrigger>
            <TabsTrigger value="system" className="gap-2">
              <Activity className="w-4 h-4" />
              {t('staffPortal.tabs.system', 'System')}
            </TabsTrigger>
          </TabsList>

          {/* System Tab - Hospice OS Features */}
          <TabsContent value="system">
            <HospiceOSFeatures variant="staff" />
          </TabsContent>

          {/* Caseload Tab */}
          <TabsContent value="caseload">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('staffPortal.caseload.assignedPatients', 'Assigned Patients')}
                    </CardTitle>
                    <CardDescription>
                      {t('staffPortal.caseload.description', 'View and manage your assigned patient caseload')}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder={t('staffPortal.caseload.search', 'Search patients...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <Button 
                      variant="default" 
                      onClick={() => setShowAddPatientModal(true)}
                      className="gap-2"
                      data-testid="add-patient-btn"
                    >
                      <UserPlus className="w-4 h-4" />
                      {t('staffPortal.caseload.addPatient', 'Add Patient')}
                    </Button>
                    <Button variant="outline" size="icon" onClick={loadCaseload}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredCaseload.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">{t('staffPortal.caseload.noPatientsAssigned', 'No patients assigned')}</p>
                    <p className="text-sm">
                      {isAdmin ? t('staffPortal.caseload.importOrAssign', 'Import a caseload or assign patients manually.') : t('staffPortal.caseload.contactAdmin', 'Contact your administrator to get patients assigned.')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCaseload.map((item) => {
                      const needsAttention = !item.last_checkin || 
                        (new Date() - new Date(item.last_checkin)) / (1000 * 60 * 60 * 24) > 3;
                      
                      return (
                        <div 
                          key={item.id}
                          className={`flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer ${
                            needsAttention ? 'border-amber-200 bg-amber-50/50' : ''
                          }`}
                          onClick={() => viewPatientDetails(item)}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${needsAttention ? 'bg-amber-100' : 'bg-primary/10'}`}>
                              <User className={`w-5 h-5 ${needsAttention ? 'text-amber-600' : 'text-primary'}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{item.patient_name}</p>
                                <Badge variant="outline" className="text-xs">
                                  {item.assignment_type}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{item.patient_email}</p>
                              {item.patient?.disease_category && (
                                <Badge variant="secondary" className="mt-1 text-xs">
                                  {item.patient.disease_category.replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm">
                                {item.last_checkin ? (
                                  <span className={needsAttention ? 'text-amber-600' : 'text-muted-foreground'}>
                                    {new Date(item.last_checkin).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <span className="text-amber-600">{t('staffPortal.caseload.noCheckins', 'No check-ins')}</span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">{t('staffPortal.caseload.lastActivity', 'Last activity')}</p>
                            </div>
                            {item.unread_messages > 0 && (
                              <Badge variant="destructive" className="h-6 w-6 p-0 flex items-center justify-center">
                                {item.unread_messages}
                              </Badge>
                            )}
                            {isAdmin && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); handleRemoveAssignment(item.id); }}
                              >
                                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            )}
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Conversations List */}
              <Card className="md:col-span-1">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t('staffPortal.messages.conversations', 'Conversations')}</CardTitle>
                    <Button size="sm" onClick={() => setShowNewMessageModal(true)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-2">
                  {conversations.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No conversations yet
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-[400px] overflow-y-auto">
                      {conversations.map((conv) => (
                        <div
                          key={conv.id}
                          className={`p-3 rounded-lg cursor-pointer hover:bg-muted transition-colors ${
                            selectedConversation?.id === conv.id ? 'bg-muted' : ''
                          }`}
                          onClick={() => loadConversationMessages(conv)}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-sm truncate">{conv.patient_name}</p>
                            {conv.unread_count_staff > 0 && (
                              <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                                {conv.unread_count_staff}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{conv.subject}</p>
                          {conv.last_message && (
                            <p className="text-xs text-muted-foreground truncate mt-1">{conv.last_message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Message Thread */}
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    {selectedConversation ? selectedConversation.subject : "Select a conversation"}
                    {selectedConversation && patientTyping && (
                      <span className="text-xs text-green-600 font-normal animate-pulse">
                        {selectedConversation.patient_name} is typing...
                      </span>
                    )}
                  </CardTitle>
                  {selectedConversation && (
                    <CardDescription>
                      with {selectedConversation.patient_name}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {selectedConversation ? (
                    <>
                      <div className="h-[300px] overflow-y-auto border rounded-lg p-3 mb-3 space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_role === 'staff' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] p-3 rounded-lg ${
                              msg.sender_role === 'staff' 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-muted'
                            }`}>
                              <p className="text-sm">{msg.content}</p>
                              <div className={`flex items-center gap-1 mt-1 ${
                                msg.sender_role === 'staff' ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground'
                              }`}>
                                <span className="text-xs">
                                  {new Date(msg.created_at).toLocaleString()}
                                </span>
                                {msg.sender_role === 'staff' && (
                                  msg.read ? (
                                    <CheckCheck className="w-3 h-3 text-blue-300" title={`Read ${msg.read_at || ''}`} />
                                  ) : (
                                    <Check className="w-3 h-3 opacity-50" title="Delivered" />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {/* Typing Indicator */}
                        {patientTyping && (
                          <div className="flex justify-start">
                            <div className="bg-muted rounded-lg px-4 py-3">
                              <div className="flex items-center gap-1">
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => {
                            setNewMessage(e.target.value);
                            handleStaffTyping();
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        />
                        <Button onClick={handleSendMessage}>
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                      Select a conversation to view messages
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clinical Alerts Tab */}
          <TabsContent value="clinical-alerts">
            <div className="space-y-6">
              {/* Quick Link to Hospice Academy */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="w-6 h-6 text-primary" />
                      <div>
                        <p className="font-medium">{t('staffPortal.academy.title', 'Hospice Academy')}</p>
                        <p className="text-sm text-muted-foreground">{t('staffPortal.academy.description', 'Access training courses and CEU tracking')}</p>
                      </div>
                    </div>
                    <Link to="/academy">
                      <Button>
                        <GraduationCap className="w-4 h-4 mr-2" />
                        {t('staffPortal.academy.open', 'Open Academy')}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Priority Alerts */}
              <Card className={clinicalAlerts.some(a => a.priority === "urgent") ? "border-red-200" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardCheck className="w-5 h-5" />
                        {t('staffPortal.clinicalAlerts.title', 'Clinical Documentation Alerts')}
                      </CardTitle>
                      <CardDescription>
                        {t('staffPortal.clinicalAlerts.description', 'Important clinical information that should be documented in the medical record')}
                      </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={loadClinicalAlerts} disabled={alertsLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${alertsLoading ? 'animate-spin' : ''}`} />
                      {t('common.refresh', 'Refresh')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {alertsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : clinicalAlerts.length > 0 ? (
                    <div className="space-y-3">
                      {clinicalAlerts.map((alert) => (
                        <div 
                          key={alert.id}
                          className={`p-4 rounded-lg border ${
                            alert.priority === "urgent" ? "bg-red-50 border-red-200" :
                            alert.priority === "high" ? "bg-amber-50 border-amber-200" :
                            "bg-blue-50 border-blue-200"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              {alert.priority === "urgent" ? (
                                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                              ) : alert.priority === "high" ? (
                                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                              ) : (
                                <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
                              )}
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">{alert.patient_name}</span>
                                  <Badge variant={
                                    alert.priority === "urgent" ? "destructive" :
                                    alert.priority === "high" ? "default" : "secondary"
                                  } className="text-xs">
                                    {alert.priority.toUpperCase()}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">{alert.alert_type}</Badge>
                                </div>
                                <p className="font-medium text-sm mt-1">{alert.title}</p>
                                <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>
                                {alert.data_field && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Data field: <code className="bg-muted px-1 py-0.5 rounded">{alert.data_field}</code>
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  const patient = caseload.find(p => p.patient_id === alert.patient_id);
                                  if (patient) viewPatientDetails(patient);
                                }}
                              >
                                {t('staffPortal.clinicalAlerts.viewPatient', 'View Patient')}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDismissAlert(alert.id)}
                              >
                                <X className="w-4 h-4 mr-1" />
                                {t('staffPortal.clinicalAlerts.dismiss', 'Dismiss')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                      <p className="font-medium">{t('staffPortal.clinicalAlerts.allClear', 'All Clear!')}</p>
                      <p className="text-sm mt-1">{t('staffPortal.clinicalAlerts.noAlerts', 'No clinical documentation alerts at this time')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Documentation Reminders */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {t('staffPortal.documentation.bestPractices', 'Documentation Best Practices')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-500" />
                        {t('staffPortal.documentation.patientCheckins', 'Patient Check-ins')}
                      </h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• {t('staffPortal.documentation.painLevels', 'Document pain levels above 5/10')}</li>
                        <li>• {t('staffPortal.documentation.moodChanges', 'Note mood changes and interventions')}</li>
                        <li>• {t('staffPortal.documentation.symptomProgression', 'Record symptom progression')}</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Pill className="w-4 h-4 text-green-500" />
                        {t('staffPortal.documentation.medicationCompliance', 'Medication Compliance')}
                      </h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• {t('staffPortal.documentation.missedMedications', 'Document missed medications')}</li>
                        <li>• {t('staffPortal.documentation.nonCompliance', 'Note reasons for non-compliance')}</li>
                        <li>• {t('staffPortal.documentation.prnEffectiveness', 'Record PRN medication effectiveness')}</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-blue-500" />
                        {t('staffPortal.documentation.vitalSigns', 'Vital Signs')}
                      </h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• {t('staffPortal.documentation.vitalChanges', 'Record significant vital changes')}</li>
                        <li>• {t('staffPortal.documentation.weightFluctuations', 'Document weight fluctuations')}</li>
                        <li>• {t('staffPortal.documentation.respiratoryStatus', 'Note respiratory status changes')}</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />
                        {t('staffPortal.documentation.familyCommunication', 'Family Communication')}
                      </h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• {t('staffPortal.documentation.familyConcerns', 'Document family concerns')}</li>
                        <li>• {t('staffPortal.documentation.educationProvided', 'Record education provided')}</li>
                        <li>• {t('staffPortal.documentation.carePlanDiscussions', 'Note care plan discussions')}</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Mood Trends Tab */}
          <TabsContent value="mood-trends">
            <div className="space-y-6">
              {/* Alerts Section */}
              {moodTrends?.alerts?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-5 h-5" />
                      {t('staffPortal.moodTrends.attentionNeeded', 'Attention Needed')} ({moodTrends.alerts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {moodTrends.alerts.map((alert, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                          <div className="flex items-center gap-3">
                            {alert.type === 'high_pain' ? (
                              <AlertCircle className="w-5 h-5 text-red-500" />
                            ) : (
                              <Frown className="w-5 h-5 text-amber-500" />
                            )}
                            <div>
                              <p className="font-medium">{alert.patient_name}</p>
                              <p className="text-sm text-muted-foreground">{alert.message}</p>
                            </div>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => {
                            const patient = caseload.find(p => p.patient_id === alert.patient_id);
                            if (patient) viewPatientDetails(patient);
                          }}>
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mood Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    {t('staffPortal.moodTrends.distribution', 'Mood Distribution (Last 30 Days)')}
                  </CardTitle>
                  <CardDescription>
                    {t('staffPortal.moodTrends.checkinsFrom', '{{count}} check-ins from {{patients}} patients', { count: moodTrends?.summary?.total_checkins || 0, patients: moodTrends?.summary?.patients_tracked || 0 })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {moodTrendsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : moodTrends?.mood_distribution ? (
                    <div className="space-y-3">
                      {Object.entries(moodTrends.mood_distribution)
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([mood, data]) => {
                          const moodColors = {
                            happy: 'bg-green-500', calm: 'bg-blue-500', grateful: 'bg-purple-500',
                            hopeful: 'bg-teal-500', content: 'bg-emerald-500',
                            anxious: 'bg-amber-500', sad: 'bg-indigo-500', frustrated: 'bg-orange-500',
                            tired: 'bg-gray-500', scared: 'bg-red-400', angry: 'bg-red-600'
                          };
                          return (
                            <div key={mood} className="flex items-center gap-3">
                              <span className="w-24 text-sm capitalize">{t(`moods.${mood}`, mood)}</span>
                              <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${moodColors[mood] || 'bg-primary'} transition-all`}
                                  style={{ width: `${data.percentage}%` }}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground w-16 text-right">
                                {data.count} ({data.percentage}%)
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">{t('staffPortal.moodTrends.noData', 'No mood data available')}</p>
                  )}
                </CardContent>
              </Card>

              {/* Latest Patient Moods */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    {t('staffPortal.moodTrends.latestPatientMoods', 'Latest Patient Moods')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {moodTrends?.patient_latest_moods?.length > 0 ? (
                    <div className="space-y-2">
                      {moodTrends.patient_latest_moods.slice(0, 10).map((patient, idx) => {
                        const moodEmoji = {
                          happy: '😊', calm: '😌', grateful: '🙏', hopeful: '✨', content: '😊',
                          anxious: '😰', sad: '😢', frustrated: '😤', tired: '😴', scared: '😨', angry: '😠'
                        };
                        const isNegative = ['anxious', 'sad', 'frustrated', 'scared', 'angry'].includes(patient.mood);
                        
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${isNegative ? 'border-amber-200 bg-amber-50/50' : 'bg-muted/30'}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{moodEmoji[patient.mood] || '😐'}</span>
                              <div>
                                <p className="font-medium">{patient.patient_name}</p>
                                <p className="text-sm text-muted-foreground capitalize">
                                  {t(`moods.${patient.mood}`, patient.mood)} {patient.pain_level !== null && `• ${t('staffPortal.moodTrends.pain', 'Pain')}: ${patient.pain_level}/10`}
                                </p>
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(patient.timestamp).toLocaleDateString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">{t('staffPortal.moodTrends.noRecentData', 'No recent mood data')}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Acuity Trends Tab */}
          <TabsContent value="acuity-trends">
            <PatientAcuityTrendsDashboard />
          </TabsContent>

          {/* Appreciation Tab */}
          <TabsContent value="appreciation">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Stats Card */}
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                    {t('staffPortal.appreciation.yourStats', 'Your Appreciation Stats')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center mb-6">
                    <div className="text-5xl font-bold text-amber-600">
                      {appreciations?.stats?.average_rating?.toFixed(1) || '0.0'}
                    </div>
                    <div className="flex justify-center gap-1 my-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star 
                          key={star}
                          className={`w-6 h-6 ${star <= (appreciations?.stats?.average_rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground">
                      {t('staffPortal.appreciation.fromCount', 'from {{count}} appreciation(s)', { count: appreciations?.stats?.total || 0 })}
                    </p>
                  </div>
                  
                  {appreciations?.stats?.by_category && Object.keys(appreciations.stats.by_category).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-800">{t('staffPortal.appreciation.byCategory', 'By Category')}:</p>
                      {Object.entries(appreciations.stats.by_category).map(([cat, data]) => (
                        <div key={cat} className="flex justify-between items-center text-sm">
                          <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            {data.avg_rating.toFixed(1)} ({data.count})
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Appreciations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ThumbsUp className="w-5 h-5" />
                    {t('staffPortal.appreciation.recentAppreciations', 'Recent Appreciations')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {appreciations?.appreciations?.length > 0 ? (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {appreciations.appreciations.slice(0, 10).map((app, idx) => (
                        <div key={idx} className="p-4 bg-muted/30 rounded-lg border">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex gap-1">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star 
                                  key={star}
                                  className={`w-4 h-4 ${star <= app.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                            <Badge variant="outline" className="text-xs capitalize">
                              {app.category.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm mb-2">&ldquo;{app.message}&rdquo;</p>
                          <div className="flex justify-between items-center text-xs text-muted-foreground">
                            <span>{t('staffPortal.appreciation.from', 'From')}: {app.caregiver_name}</span>
                            <span>{new Date(app.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Heart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>{t('staffPortal.appreciation.noAppreciations', 'No appreciations yet')}</p>
                      <p className="text-sm">{t('staffPortal.appreciation.keepUp', 'Keep up the great work!')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-2xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    {t('staffPortal.smsSettings.title', 'SMS Notification Settings')}
                  </CardTitle>
                  <CardDescription>
                    {t('staffPortal.smsSettings.description', 'Receive text message alerts when patients send you messages')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {smsSettingsLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {t('staffPortal.smsSettings.phoneNumber', 'Phone Number')}
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={smsSettings.phone}
                          onChange={(e) => setSmsSettings({...smsSettings, phone: e.target.value})}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('staffPortal.smsSettings.usPhoneNumber', 'US phone number for receiving SMS notifications')}
                        </p>
                      </div>

                      {/* SMS Toggle */}
                      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                        <div className="space-y-1">
                          <p className="font-medium">{t('staffPortal.smsSettings.textMessageNotifications', 'Text Message Notifications')}</p>
                          <p className="text-sm text-muted-foreground">
                            {t('staffPortal.smsSettings.receiveAlerts', 'Receive SMS alerts when patients send you messages')}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={smsSettings.sms_notifications}
                            onChange={(e) => setSmsSettings({...smsSettings, sms_notifications: e.target.checked})}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                      </div>

                      {/* Info Box */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-blue-800">{t('staffPortal.smsSettings.howItWorks', 'How it works')}</p>
                            <p className="text-blue-700 mt-1">
                              {t('staffPortal.smsSettings.howItWorksDescription', "When a patient sends you a message through the portal, you'll receive a text message with a preview of their message. This helps you respond quickly to patient needs.")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <Button 
                        onClick={handleSaveSmsSettings} 
                        disabled={savingSmsSettings}
                        className="w-full"
                      >
                        {savingSmsSettings ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                        )}
                        {t('staffPortal.smsSettings.saveSettings', 'Save Settings')}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Patient Details Dialog */}
      <Dialog open={!!selectedPatient} onOpenChange={() => { setSelectedPatient(null); setPatientDetails(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedPatient?.patient_name} - {t('staffPortal.patientDetails.title', 'Patient Details')}
            </DialogTitle>
            <DialogDescription>
              {t('staffPortal.patientDetails.description', 'View comprehensive patient information and activity')}
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : patientDetails ? (
            <div className="space-y-6">
              {/* Patient Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('staffPortal.patientDetails.email', 'Email')}:</span>
                    <p className="font-medium">{patientDetails.patient?.email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('staffPortal.patientDetails.condition', 'Condition')}:</span>
                    <p className="font-medium capitalize">
                      {patientDetails.patient?.disease_category?.replace(/_/g, ' ') || t('staffPortal.patientDetails.notSpecified', 'Not specified')}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('staffPortal.patientDetails.phone', 'Phone')}:</span>
                    <p className="font-medium">{patientDetails.patient?.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Recent Check-ins */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  {t('staffPortal.patientDetails.recentCheckins', 'Recent Check-ins')} ({patientDetails.checkins?.length || 0})
                </h3>
                {patientDetails.checkins?.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {patientDetails.checkins.slice(0, 5).map((checkin, idx) => (
                      <div key={idx} className="p-3 bg-muted/30 rounded-lg border flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium">
                            {t('staffPortal.patientDetails.painLevel', 'Pain')}: {checkin.pain_level}/10 | {t('staffPortal.patientDetails.mood', 'Mood')}: {checkin.mood || 'N/A'}
                          </p>
                          {checkin.symptoms?.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('staffPortal.patientDetails.symptoms', 'Symptoms')}: {checkin.symptoms.join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(checkin.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t('staffPortal.patientDetails.noCheckins', 'No check-ins recorded')}</p>
                )}
              </div>

              {/* Medications */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  {t('staffPortal.patientDetails.currentMedications', 'Current Medications')} ({patientDetails.medications?.length || 0})
                </h3>
                {patientDetails.medications?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {patientDetails.medications.map((med, idx) => (
                      <div key={idx} className="p-2 bg-muted/30 rounded border text-sm">
                        <p className="font-medium">{med.name}</p>
                        <p className="text-xs text-muted-foreground">{med.dosage} - {med.frequency}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t('staffPortal.patientDetails.noMedications', 'No medications listed')}</p>
                )}
              </div>

              {/* Journal Entries Summary */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('staffPortal.patientDetails.recentJournals', 'Recent Journals')} ({patientDetails.journals?.length || 0})
                </h3>
                {patientDetails.journals?.length > 0 ? (
                  <div className="space-y-1">
                    {patientDetails.journals.slice(0, 5).map((journal, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                        <span className="text-sm">
                          {journal.observations?.slice(0, 50)}... | {t('staffPortal.patientDetails.mood', 'Mood')}: {journal.mood || 'N/A'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(journal.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">{t('staffPortal.patientDetails.noJournals', 'No journal entries')}</p>
                )}
              </div>

              {/* Care Insights */}
              {patientDetails.care_insights?.length > 0 && (
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    {t('staffPortal.patientDetails.aiCareInsights', 'AI Care Insights')}
                  </h3>
                  <div className="space-y-2">
                    {patientDetails.care_insights.slice(0, 3).map((insight, idx) => (
                      <div key={idx} className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-sm">{insight.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(insight.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('staffPortal.import.title', 'Import Caseload')}</DialogTitle>
            <DialogDescription>
              {t('staffPortal.import.description', 'Upload a CSV or Excel file with staff-patient assignments')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('staffPortal.import.selectFile', 'Select File')}</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0])}
                className="mt-1"
              />
            </div>
            
            <Button variant="outline" onClick={downloadTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              {t('staffPortal.import.downloadTemplate', 'Download Template')}
            </Button>
            
            {importResults && (
              <div className="p-3 bg-muted rounded-lg text-sm space-y-2">
                <p className="font-medium">{t('staffPortal.import.results', 'Import Results')}:</p>
                <div className="flex gap-4">
                  <p className="text-green-600">✓ {importResults.successful} {t('staffPortal.import.successful', 'successful')}</p>
                  {importResults.failed > 0 && (
                    <p className="text-red-600">✗ {importResults.failed} {t('staffPortal.import.failed', 'failed')}</p>
                  )}
                </div>
                
                {/* Show successful matches */}
                {importResults.assignments_created?.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{t('staffPortal.import.assignmentsCreated', 'Assignments Created')}:</p>
                    <div className="text-xs space-y-1 max-h-24 overflow-y-auto">
                      {importResults.assignments_created.map((a, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-green-700">
                          <span>✓ {a.staff} → {a.patient}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {a.staff_match}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {importResults.errors?.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="text-xs font-medium text-red-600 mb-1">{t('staffPortal.import.errors', 'Errors')}:</p>
                    <div className="text-xs text-muted-foreground max-h-20 overflow-y-auto">
                      {importResults.errors.slice(0, 5).map((err, idx) => (
                        <p key={idx} className="text-red-600">{t('staffPortal.import.row', 'Row')} {err.row}: {err.error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handleImportCaseload} disabled={!importFile || importing}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {t('staffPortal.import.import', 'Import')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Patient Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('staffPortal.assign.title', 'Assign Patient to Staff')}</DialogTitle>
            <DialogDescription>
              {t('staffPortal.assign.description', 'Create a new staff-patient assignment')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('staffPortal.assign.staffMember', 'Staff Member')}</Label>
              <Select value={assignmentData.staffId} onValueChange={(v) => setAssignmentData({...assignmentData, staffId: v})}>
                <SelectTrigger><SelectValue placeholder={t('staffPortal.assign.selectStaff', 'Select staff...')} /></SelectTrigger>
                <SelectContent>
                  {staffList.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.email}) - {s.caseload_count} {t('staffPortal.assign.patients', 'patients')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('staffPortal.assign.patient', 'Patient')}</Label>
              <Select value={assignmentData.patientId} onValueChange={(v) => setAssignmentData({...assignmentData, patientId: v})}>
                <SelectTrigger><SelectValue placeholder={t('staffPortal.assign.selectPatient', 'Select patient...')} /></SelectTrigger>
                <SelectContent>
                  {patientsList.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('staffPortal.assign.assignmentType', 'Assignment Type')}</Label>
              <Select value={assignmentData.type} onValueChange={(v) => setAssignmentData({...assignmentData, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">{t('staffPortal.assign.primary', 'Primary')}</SelectItem>
                  <SelectItem value="secondary">{t('staffPortal.assign.secondary', 'Secondary')}</SelectItem>
                  <SelectItem value="backup">{t('staffPortal.assign.backup', 'Backup')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('staffPortal.assign.notes', 'Notes (optional)')}</Label>
              <Textarea 
                value={assignmentData.notes}
                onChange={(e) => setAssignmentData({...assignmentData, notes: e.target.value})}
                placeholder={t('staffPortal.assign.notesPlaceholder', 'Add any notes about this assignment...')}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handleAssign}>
              <UserPlus className="w-4 h-4 mr-2" />
              {t('staffPortal.assign.assign', 'Assign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('staffPortal.newConversation.title', 'New Conversation')}</DialogTitle>
            <DialogDescription>
              {t('staffPortal.newConversation.description', 'Start a conversation with an assigned patient')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('staffPortal.newConversation.patient', 'Patient')}</Label>
              <Select 
                value={newConversation.patientId} 
                onValueChange={(v) => setNewConversation({...newConversation, patientId: v})}
              >
                <SelectTrigger><SelectValue placeholder={t('staffPortal.newConversation.selectPatient', 'Select patient...')} /></SelectTrigger>
                <SelectContent>
                  {caseload.map(p => (
                    <SelectItem key={p.patient_id} value={p.patient_id}>
                      {p.patient_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t('staffPortal.newConversation.subject', 'Subject')}</Label>
              <Input 
                value={newConversation.subject}
                onChange={(e) => setNewConversation({...newConversation, subject: e.target.value})}
                placeholder={t('staffPortal.newConversation.subjectPlaceholder', 'Conversation subject...')}
              />
            </div>
            
            <div>
              <Label>{t('staffPortal.newConversation.message', 'Message')}</Label>
              <Textarea 
                value={newConversation.message}
                onChange={(e) => setNewConversation({...newConversation, message: e.target.value})}
                placeholder={t('staffPortal.newConversation.messagePlaceholder', 'Your message...')}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handleCreateConversation}>
              <Send className="w-4 h-4 mr-2" />
              {t('staffPortal.newConversation.start', 'Start Conversation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Patient Modal */}
      <Dialog open={showAddPatientModal} onOpenChange={closeAddPatientModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              {t('staffPortal.addPatient.title', 'Add New Patient')}
            </DialogTitle>
            <DialogDescription>
              {t('staffPortal.addPatient.description', 'Manually create a new patient record. A temporary password will be generated.')}
            </DialogDescription>
          </DialogHeader>
          
          {createdPatient ? (
            /* Show credentials after successful creation */
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-800">{t('staffPortal.addPatient.success', 'Patient Created Successfully!')}</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="text-muted-foreground">{t('staffPortal.addPatient.name', 'Name')}:</span>
                    <span className="font-medium">{createdPatient.name}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="text-muted-foreground">{t('staffPortal.addPatient.emailLabel', 'Email')}:</span>
                    <span className="font-medium">{createdPatient.email}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-amber-50 rounded border border-amber-200">
                    <span className="text-amber-700">{t('staffPortal.addPatient.tempPassword', 'Temporary Password')}:</span>
                    <span className="font-mono font-bold text-amber-800">{createdPatient.temp_password}</span>
                  </div>
                  
                  {/* SMS Status */}
                  {createdPatient.phone && (
                    <div className={`flex justify-between items-center p-2 rounded border ${
                      createdPatient.sms_sent 
                        ? "bg-blue-50 border-blue-200" 
                        : "bg-gray-50 border-gray-200"
                    }`}>
                      <span className={createdPatient.sms_sent ? "text-blue-700" : "text-gray-600"}>
                        📱 {t('staffPortal.addPatient.smsNotification', 'SMS Notification')}:
                      </span>
                      <span className={`font-medium ${
                        createdPatient.sms_sent ? "text-blue-800" : "text-gray-600"
                      }`}>
                        {createdPatient.sms_sent ? t('staffPortal.addPatient.sentToPatient', '✓ Sent to patient') : t('staffPortal.addPatient.notSent', 'Not sent')}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  ⚠️ {t('staffPortal.addPatient.shareCredentials', 'Please share these login credentials with the patient. They will be asked to change their password on first login.')}
                  {createdPatient.sms_sent && (
                    <span className="block mt-1 text-blue-600">
                      ✓ {t('staffPortal.addPatient.credentialsSent', "Credentials have been sent to the patient's phone via SMS.")}
                    </span>
                  )}
                </p>
              </div>
              
              <DialogFooter>
                <Button onClick={closeAddPatientModal}>
                  <Check className="w-4 h-4 mr-2" />
                  {t('staffPortal.addPatient.done', 'Done')}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Patient creation form */
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="patient-name">{t('staffPortal.addPatient.fullName', 'Full Name')} *</Label>
                  <Input
                    id="patient-name"
                    placeholder={t('staffPortal.addPatient.fullNamePlaceholder', 'John Doe')}
                    value={newPatientData.name}
                    onChange={(e) => setNewPatientData({...newPatientData, name: e.target.value})}
                    data-testid="patient-name-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="patient-email">{t('staffPortal.addPatient.email', 'Email Address')} *</Label>
                  <Input
                    id="patient-email"
                    type="email"
                    placeholder={t('staffPortal.addPatient.emailPlaceholder', 'patient@example.com')}
                    value={newPatientData.email}
                    onChange={(e) => setNewPatientData({...newPatientData, email: e.target.value})}
                    data-testid="patient-email-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="patient-phone">{t('staffPortal.addPatient.phone', 'Phone')}</Label>
                  <Input
                    id="patient-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newPatientData.phone}
                    onChange={(e) => setNewPatientData({...newPatientData, phone: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="patient-dob">{t('staffPortal.addPatient.dob', 'Date of Birth')}</Label>
                  <Input
                    id="patient-dob"
                    type="date"
                    value={newPatientData.date_of_birth}
                    onChange={(e) => setNewPatientData({...newPatientData, date_of_birth: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="patient-address">{t('staffPortal.addPatient.address', 'Address')}</Label>
                  <Input
                    id="patient-address"
                    placeholder={t('staffPortal.addPatient.addressPlaceholder', '123 Main St, City, State')}
                    value={newPatientData.address}
                    onChange={(e) => setNewPatientData({...newPatientData, address: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="patient-diagnosis">{t('staffPortal.addPatient.diagnosis', 'Primary Diagnosis')}</Label>
                  <Input
                    id="patient-diagnosis"
                    placeholder={t('staffPortal.addPatient.diagnosisPlaceholder', 'Enter primary diagnosis...')}
                    value={newPatientData.diagnosis}
                    onChange={(e) => setNewPatientData({...newPatientData, diagnosis: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="emergency-name">{t('staffPortal.addPatient.emergencyContactName', 'Emergency Contact Name')}</Label>
                  <Input
                    id="emergency-name"
                    placeholder={t('staffPortal.addPatient.emergencyNamePlaceholder', 'Jane Doe')}
                    value={newPatientData.emergency_contact_name}
                    onChange={(e) => setNewPatientData({...newPatientData, emergency_contact_name: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="emergency-phone">{t('staffPortal.addPatient.emergencyContactPhone', 'Emergency Contact Phone')}</Label>
                  <Input
                    id="emergency-phone"
                    type="tel"
                    placeholder="(555) 987-6543"
                    value={newPatientData.emergency_contact_phone}
                    onChange={(e) => setNewPatientData({...newPatientData, emergency_contact_phone: e.target.value})}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="patient-notes">{t('staffPortal.addPatient.notes', 'Notes')}</Label>
                  <Textarea
                    id="patient-notes"
                    placeholder={t('staffPortal.addPatient.notesPlaceholder', 'Additional notes about the patient...')}
                    value={newPatientData.notes}
                    onChange={(e) => setNewPatientData({...newPatientData, notes: e.target.value})}
                    rows={2}
                  />
                </div>
              </div>
              
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeAddPatientModal}>{t('common.cancel', 'Cancel')}</Button>
                <Button 
                  onClick={handleAddPatient} 
                  disabled={addingPatient || !newPatientData.name || !newPatientData.email}
                  data-testid="create-patient-btn"
                >
                  {addingPatient ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  {t('staffPortal.addPatient.createPatient', 'Create Patient')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
